import React, { useContext, useEffect } from 'react';
import { connect, ReactReduxContext } from 'react-redux';

import { AppContext } from '@prisjakt/ui-app-context';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';

import NewProductReleases from '../../components/new-product-releases';
import CampaignDeals from '../../components/campaign-deals';
import CampaignBanner from './components/campaign-banner';
import ForumTopicListing from './components/forum-topic-listing';
import PageContent from '../../components/page-content';
import FeaturedArticle from './components/featured-article';
import PromoBanner from '../../components/campaign-banners/promo-banner';
import RecentlyVisitedProducts from '../../components/recently-visited';
import { CategoryList } from '../../components/category-list';
import ProductRecommendations from './components/product-recommendations';
import AdContainer from '../../components/ads';
import SotyBanner from './components/soty-banner';

import { plainText } from '../editorial-content/components/slices/rich-text';

import {
  LandingPageQuery,
  Campaign,
  CampaignBanner as CampaignBannerType,
} from '../../types';
import { AppState } from '../../reducers';

import * as S from './styles';

type OwnProps = {
  data: LandingPageQuery;
};

type StateProps = {
  activeCampaign?: Campaign;
  bigCampaignBanner?: CampaignBannerType;
  campaignBanners?: AppState['globals']['campaignBanners'];
};

type Props = OwnProps & StateProps;

const LandingPage = ({
  bigCampaignBanner,
  activeCampaign,
  campaignBanners,
  data,
}: Props) => {
  const { t, market } = useContext(AppContext);
  const { store } = useContext(ReactReduxContext);

  const {
    campaignDeals,
    forumTopics,
    personalizedRecommendations,
    fallbackRecommendations,
    newProductRecommendations,
    featuredArticle,
    rootCategories,
  } = data;

  useEffect(() => {
    store.dispatch({
      type: 'ADS_VIEW_UPDATE',
      view: 'landing-page:mounted',
    });
  }, [store]);

  const categories = (rootCategories || [])
    .map(category =>
      category
        ? {
            ...category,
            logo: { 192: category.logo },
          }
        : null
    )
    .filter(x => x);

  const activeCampaignLogo =
    activeCampaign &&
    activeCampaign.banners &&
    activeCampaign.banners.find(
      banner => banner && banner.type.toUpperCase() === 'BANNER_LOGO_BIG'
    );

  const activeCampaignAsCategory = activeCampaign && {
    id: 'activeCampaign',
    name: activeCampaign.name,
    pathName: activeCampaign.web_uri && activeCampaign.web_uri.responsive,
    logo: {
      192: activeCampaignLogo ? activeCampaignLogo.imageUrl : null,
    },
  };

  const beforeBanners =
    campaignBanners &&
    campaignBanners.active.filter(
      (b: CampaignBannerType) => b && b.type === 'BEFORE_PROMO_BANNER'
    );

  const categoriesWithActiveCampaign = activeCampaignAsCategory
    ? [activeCampaignAsCategory, ...categories]
    : categories;

  return (
    <S.LandingPage>
      <PageContent withAds headerHeight={0} bottomPlacements={[]}>
        <S.Content>
          <Panel
            header={<PanelHeader title={t('landing', 'categories.title')} />}
            data-test-type="categories"
          >
            <CategoryList withAds categories={categoriesWithActiveCampaign} />
          </Panel>

          {bigCampaignBanner && (
            <CampaignBanner
              url={bigCampaignBanner.url}
              imageUrl={bigCampaignBanner.imageUrl}
              {...bigCampaignBanner.options}
            />
          )}

          {beforeBanners && beforeBanners.length > 0 && (
            <PromoBanner inverted banners={beforeBanners} />
          )}

          {market.shopOfTheYear?.isVotingOpen && (
            <SotyBanner year={market.shopOfTheYear.year} />
          )}

          {data.prismicArticles &&
            data.prismicArticles.nodes.length > 0 &&
            data.prismicArticles.nodes.map((article, index) => {
              const ArticleComponent = (
                <Panel key={article.id} header={<PanelHeader />}>
                  <FeaturedArticle
                    pathName={article.pathName}
                    imageUrl={
                      article.data.featuredImage &&
                      article.data.featuredImage.url
                    }
                    title={plainText(article.data.title)}
                    preamble={plainText(article.data.metaDescription)}
                    linkText={t('landing', 'featured-article.read-more')}
                  />
                </Panel>
              );

              if (index === 0) {
                return (
                  <>
                    {ArticleComponent}
                    <AdContainer
                      placement="dubbelpanorama2"
                      format="panorama_2"
                      min={0}
                      max={860}
                      spacing={{ bottom: '20px' }}
                      triggerPoint={100}
                    />
                  </>
                );
              }

              return ArticleComponent;
            })}

          {featuredArticle && (
            <Panel header={<PanelHeader />}>
              <FeaturedArticle
                {...featuredArticle}
                linkText={t('landing', 'featured-article.read-more')}
              />
            </Panel>
          )}

          <RecentlyVisitedProducts />

          {fallbackRecommendations && fallbackRecommendations.products && (
            <ProductRecommendations {...fallbackRecommendations} />
          )}

          <CampaignDeals deals={campaignDeals} />

          {personalizedRecommendations &&
            personalizedRecommendations.products &&
            !personalizedRecommendations.products.fallbackToDefault && (
              <ProductRecommendations {...personalizedRecommendations} />
            )}

          {newProductRecommendations && (
            <NewProductReleases {...newProductRecommendations} />
          )}

          <AdContainer
            placement="dubbelpanorama3"
            format="panorama_3"
            min={0}
            max={860}
            spacing={{ bottom: '20px' }}
            triggerPoint={100}
          />

          {forumTopics && <ForumTopicListing topics={forumTopics} />}
        </S.Content>
      </PageContent>
    </S.LandingPage>
  );
};

const mapStateToProps = (state: AppState) => ({
  activeCampaign:
    state.globals.campaigns &&
    state.globals.campaigns.active.find(
      (c: Campaign) => c.type === 'OFFER' || c.type === 'SEARCH_BANNER'
    ),
  bigCampaignBanner:
    state.globals.campaignBanners &&
    state.globals.campaignBanners.active.find(
      (banner: CampaignBannerType) => banner.type === 'LANDING_PAGE_BIG'
    ),
  campaignBanners: state.globals.campaignBanners,
});

const dispatchActions = {};

export default connect(mapStateToProps, dispatchActions)(LandingPage);
